/**
 * The Materials ⇄ Service toggle that sits in both headers.
 *
 * Renders NOTHING for somebody entitled to one side only, which is most of the
 * company. A store-man must not see a button that hints at a room he cannot
 * enter, and a button that would show him a locked card is worse than no
 * button.
 *
 * It lives in components/layout, not in either module's own folder, because
 * both shells use it and neither owns it. It is the seam, and the seam belongs
 * to the frame. scripts/check-module-boundary.py allows exactly this one file
 * to know about both sides - see its EXEMPT list.
 *
 * ---------------------------------------------------------------------------
 * THE COUNT ON IT — board, 17-09-2026
 *
 * The bell was split that day: each side shows its own notifications and
 * nothing of the other's. That is right, and it leaves one hole. Perhaps ten
 * people in the company work on both sides - the Procurement Managers, the
 * Head of Procurement, the Project Head, the CMD - and before the split their
 * one bell told them about everything. After it, a buyer standing in Service
 * Procurement could have three purchase orders waiting for his approval and no
 * way to know without going and looking.
 *
 * So the count moves onto the seam. This button says HOW MANY are waiting on
 * the other side and nothing whatever about what they are: no titles, no
 * document numbers, no list. The two lists stay completely apart, which is what
 * was asked for; the person still learns that there is a reason to cross, which
 * is what they need.
 *
 * It is drawn only for people who have both sides, because it is part of a
 * button only they can see.
 * ---------------------------------------------------------------------------
 */
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeftRight, HardHat, Landmark, Package } from 'lucide-react'
import clsx from 'clsx'
import { useAuth } from '@/lib/auth'
import { supabase } from '@/lib/supabase'
import { moduleFor, modulesFor, rememberModule, type ModuleKey } from '@/lib/modules'

export function ModuleSwitch({ current }: { current: ModuleKey }) {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [waiting, setWaiting] = useState<Record<string, number>>({})

  // Project Finance (01-10-2026) made it possible to have TWO other sides, so
  // there is now one button per other side. For everybody with two sides that
  // is one button, exactly as before.
  const mine = modulesFor(profile?.role)
  const others = mine.length > 1 ? mine.filter((m) => m.key !== current) : []
  const otherKeys = others.map((m) => m.key).join(',')

  /**
   * How many are unread on each OTHER side.
   *
   * v_my_unread_counts is one row of integers and is counted over every
   * unread notification rather than over a page of them, so this number is the
   * truth rather than "however many of the last forty". Polled on the same
   * sixty seconds as the bell, for the same reason: a poll that stops shows a
   * stale number, where a dropped socket shows nothing and says nothing.
   * Project Finance's count is finance_unread, appended by migration 0193.
   */
  const load = useCallback(async () => {
    if (!profile || !otherKeys) return
    const { data } = await supabase.from('v_my_unread_counts').select('*').maybeSingle()
    const c: any = data ?? {}
    setWaiting({
      service: Number(c.service_unread) || 0,
      materials: Number(c.materials_unread) || 0,
      // 0193 appended finance_unread; absent on a database without it, so 0.
      finance: Number(c.finance_unread) || 0,
    })
  }, [profile, otherKeys])

  useEffect(() => {
    void load()
    const t = setInterval(() => { void load() }, 60_000)
    return () => clearInterval(t)
  }, [load])

  // One side only: no seam to cross, so no control. AFTER the hooks, never
  // before - see scripts/check-hooks.py.
  if (others.length === 0) return null

  return (
    <>
      {others.map((other) => (
        <SwitchButton key={other.key} other={other} waiting={waiting[other.key] ?? 0}
                      onGo={() => { rememberModule(other.key); navigate(moduleFor(other.key).home) }} />
      ))}
    </>
  )
}

const SHORT: Record<ModuleKey, string> = { materials: 'Materials', service: 'Service', finance: 'Finance' }
const ICON: Record<ModuleKey, typeof Package> = { materials: Package, service: HardHat, finance: Landmark }

function SwitchButton({ other, waiting, onGo }: {
  other: ReturnType<typeof moduleFor>; waiting: number; onGo: () => void
}) {
  const Icon = ICON[other.key]
  return (
    <button
      type="button"
      onClick={onGo}
      title={waiting > 0
        ? `Switch to ${other.title} — ${waiting} notification${waiting === 1 ? '' : 's'} waiting there`
        : `Switch to ${other.title}`}
      className={clsx(
        'relative flex shrink-0 items-center gap-1 rounded-lg border px-1.5 py-1.5 text-sm transition-colors sm:gap-1.5 sm:px-2',
        other.key === 'service'
          // Service in graphite since the 30-09-2026 branding (was amber).
          ? 'border-slate-300 text-slate-700 hover:bg-slate-100'
          : 'border-oval-200 text-oval-800 hover:bg-oval-50',
      )}
    >
      <Icon className="h-4 w-4" />
      {/* The full name on a laptop, the short one on a tablet, and on a phone
          just the two-way arrow - which is the universal sign for this and
          costs no width at all. */}
      <span className="hidden lg:inline">{other.title}</span>
      <span className="hidden sm:inline lg:hidden">{SHORT[other.key]}</span>
      <ArrowLeftRight className="h-3.5 w-3.5 sm:hidden" />

      {/* A NUMBER, AND NOTHING ELSE. What is waiting over there is over there. */}
      {waiting > 0 && (
        <span
          aria-label={`${waiting} unread on ${other.title}`}
          className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white"
        >
          {waiting > 99 ? '99+' : waiting}
        </span>
      )}
    </button>
  )
}
